import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  type Edge,
  type OnEdgesChange,
  type OnNodesChange,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { edgeTypes } from './edgeTypes'
import { nodeTypes } from './nodeTypes'
import { TriggerPlaceholder } from './nodes/TriggerPlaceholder'
import { useIsDarkMode } from 'app/hooks/useIsDarkMode'
import type { WorkflowNode } from 'app/types/workflows'

const defaultEdgeOptions = { type: `connector`, animated: true }

/** Below this many blocks the whole workflow fits on screen, so a minimap is noise. Recall (17
 *  blocks) gets one; a three-step follow-up doesn't. */
const MINIMAP_NODE_THRESHOLD = 5

type WorkflowCanvasProps = {
  nodes: WorkflowNode[]
  edges: Edge[]
  onNodesChange: OnNodesChange<WorkflowNode>
  onEdgesChange: OnEdgesChange
  onChooseTrigger: () => void
  /** Whether the trigger picker is open in the inspector, so the placeholder card can say so. */
  pickingTrigger: boolean
}

/** Presentational React Flow canvas in the structured "auto-connect" model: nodes are laid out for you
 *  and can't be dragged or hand-wired — steps are added only via the "+" affordances, which connect them
 *  automatically. This removes the free-form footguns (orphans, unreachable islands) by construction.
 *  State lives in the parent so the toolbar can mutate it. */
export function WorkflowCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onChooseTrigger,
  pickingTrigger,
}: WorkflowCanvasProps) {
  const isDark = useIsDarkMode()
  return (
    <div className="relative flex-1">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodesDraggable={false}
        nodesConnectable={false}
        deleteKeyCode={null}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        // Matches React Flow's own light/dark styling (controls, minimap chrome, edge defaults)
        // to the app's resolved theme, since the library's `.light`/`.dark` wrapper class is
        // otherwise stuck on `.light` regardless of the app's actual theme.
        colorMode={isDark ? `dark` : `light`}
        // Hides React Flow's corner watermark. Permitted by its MIT licence (which governs the
        // copyright notice in source, not a badge in the UI); the library asks for a Pro
        // subscription from teams that hide it, which is a support question, not a legal one.
        proOptions={{ hideAttribution: true }}
      >
        {/* gap mirrors --dot-gap (20px); React Flow only takes a number here. */}
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="var(--dot-ink)" />
        <Controls
          showInteractive={false}
          className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm [&>button]:border-subtle"
        />
        {/* A minimap only earns its space once the canvas outgrows the viewport. On a short workflow
            it was a blank white rectangle sitting on the dots, which read as a rendering bug. */}
        {nodes.length > MINIMAP_NODE_THRESHOLD && (
          <MiniMap
            pannable
            zoomable
            className="hidden overflow-hidden rounded-lg border border-default shadow-sm md:block"
            // Blocks are grayscale by design (see palette.ts), so the map matches: the trigger reads
            // a shade darker than the steps, which is enough to orient by. The raw gray-400/gray-300
            // scale, not the `--gray-default`/`--gray-subtle` roles, which read a step too dark.
            nodeColor={(node) => (node.type === `trigger` ? `var(--gray-400)` : `var(--gray-300)`)}
            nodeStrokeWidth={0}
            maskColor="color-mix(in srgb, var(--surface) 75%, transparent)"
          />
        )}
        {/* Shown whenever the graph is HEADLESS, not only when it's empty: deleting a trigger is one
            click, and before this the only recovery path was undo — the canvas offered nothing and
            the steps sat there looking fine while the workflow could never run (08-24 audit). */}
        {!nodes.some((node) => node.type === `trigger`) && (
          <Panel position="top-center">
            <div className="mt-16">
              <TriggerPlaceholder
                picking={pickingTrigger}
                onChooseTrigger={onChooseTrigger}
                headless={nodes.length > 0}
              />
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  )
}
